import {
  AcceptedAdAssessmentsService,
  AcceptedSbAssessmentsService,
  AircraftService,
  FittedComponentService,
  type FittedComponentsPublic,
  type ModStatusPublic,
  ModStatusService,
} from "@common/client"
import { type QueryKey, queryOptions } from "@tanstack/react-query"
import { adComplianceBaseKey, sbComplianceBaseKey } from "./compliance"
import { partBalancesBaseKey } from "./part-balances"
import { partStockBaseKey } from "./part-stock"
import {
  makeAxiosMutation,
  makeInfiniteQuery,
  makeQuery,
  pagedRows,
  unwrap,
} from "./utils"

export const aircraftBaseKey = ["aircraft"] as const

export const aircraftListKey = (params?: { skip?: number; limit?: number }) =>
  [...aircraftBaseKey, "list", params] as const

export const aircraftDetailKey = (id: string) =>
  [...aircraftBaseKey, "detail", id] as const

export const fittedComponentsRootKey = ["fitted-components"] as const

export const fittedComponentsBaseKey = (aircraftId: string) =>
  [...fittedComponentsRootKey, aircraftId] as const

export const allFittedComponentsKey = (aircraftId: string) =>
  [...fittedComponentsBaseKey(aircraftId), "all"] as const

export const fittedComponentsKey = (
  params: Parameters<typeof AircraftService.getFittedComponentsByAircraftId>[0],
) =>
  [
    ...fittedComponentsBaseKey(params.aircraft_id),
    params.skip,
    params.limit,
    params.major_assemblies_only ?? false,
  ] as const

export const modStatusRootKey = ["mod-status"] as const

export const modStatusBaseKey = (aircraftId: string) =>
  [...modStatusRootKey, aircraftId] as const

export type ModStatusPagesParams = Omit<
  Parameters<typeof AircraftService.getModStatusByAircraftId>[0],
  "skip" | "limit"
>

export const adComplianceAircraftKey = (aircraftId: string) =>
  [...adComplianceBaseKey, aircraftId] as const

export type AdCompliancePagesParams = Omit<
  Parameters<typeof AcceptedAdAssessmentsService.getAdComplianceForAircraft>[0],
  "skip" | "limit"
>

export type SbCompliancePagesParams = Omit<
  Parameters<typeof AcceptedSbAssessmentsService.getSbComplianceForAircraft>[0],
  "skip" | "limit"
>

export const COMPLIANCE_PAGE_SIZE = 50

const FITTED_COMPONENT_PAGE_SIZE = 1000

export const aircraftQuery = makeQuery({
  serviceCall: AircraftService.readItems,
  queryKey: (params) => aircraftListKey(params),
})

export const aircraftDetailQuery = makeQuery({
  serviceCall: AircraftService.readItem,
  queryKey: (params) => aircraftDetailKey(params.id),
})

export const aircraftByCustomerKey = (customerId: string) =>
  [...aircraftBaseKey, "by-customer", customerId] as const

export const aircraftByCustomerQuery = makeQuery({
  serviceCall: AircraftService.getAircraftByCustomer,
  queryKey: (params) => aircraftByCustomerKey(params.customer_id),
})

export const fittedComponentsQuery = makeQuery({
  serviceCall: AircraftService.getFittedComponentsByAircraftId,
  queryKey: fittedComponentsKey,
})

function fittedComponentsPage(
  aircraftId: string,
  skip: number,
): Promise<FittedComponentsPublic> {
  return AircraftService.getFittedComponentsByAircraftId({
    aircraft_id: aircraftId,
    skip,
    limit: FITTED_COMPONENT_PAGE_SIZE,
  }).then(unwrap)
}

async function allFittedComponents(
  aircraftId: string,
): Promise<FittedComponentsPublic> {
  const first = await fittedComponentsPage(aircraftId, 0)
  const skips = Array.from(
    { length: Math.ceil(first.count / FITTED_COMPONENT_PAGE_SIZE) },
    (_, index) => index * FITTED_COMPONENT_PAGE_SIZE,
  ).slice(1)
  const rest = await Promise.all(
    skips.map((skip) => fittedComponentsPage(aircraftId, skip)),
  )
  return {
    data: pagedRows([first, ...rest], (component) => component.id),
    count: first.count,
  }
}

export const allFittedComponentsQuery = (aircraftId: string) =>
  queryOptions({
    queryKey: allFittedComponentsKey(aircraftId),
    queryFn: () => allFittedComponents(aircraftId),
  })

export const modStatusInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: ModStatusPagesParams, page) =>
    AircraftService.getModStatusByAircraftId({ ...params, ...page }),
  queryKey: (params) => [
    ...modStatusBaseKey(params.aircraft_id),
    "list",
    params,
  ],
  pageSize: COMPLIANCE_PAGE_SIZE,
})

export const adComplianceInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: AdCompliancePagesParams, page) =>
    AcceptedAdAssessmentsService.getAdComplianceForAircraft({
      ...params,
      ...page,
    }),
  queryKey: (params) => [
    ...adComplianceAircraftKey(params.aircraft_id),
    "list",
    params,
  ],
  pageSize: COMPLIANCE_PAGE_SIZE,
})

export const sbComplianceAircraftKey = (aircraftId: string) =>
  [...sbComplianceBaseKey, aircraftId] as const

export const sbComplianceInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: SbCompliancePagesParams, page) =>
    AcceptedSbAssessmentsService.getSbComplianceForAircraft({
      ...params,
      ...page,
    }),
  queryKey: (params) => [
    ...sbComplianceAircraftKey(params.aircraft_id),
    "list",
    params,
  ],
  pageSize: COMPLIANCE_PAGE_SIZE,
})

export const useCreateAircraftMutation = makeAxiosMutation({
  serviceCall: AircraftService.createItem,
  invalidates: () => [aircraftBaseKey],
})

export const useUpdateAircraftMutation = makeAxiosMutation({
  serviceCall: AircraftService.updateItem,
  invalidates: (params) => [aircraftBaseKey, aircraftDetailKey(params.id)],
})

export const useCreateFittedComponentMutation = makeAxiosMutation({
  serviceCall: FittedComponentService.createItem,
  invalidates: (_, component) => [
    fittedComponentsBaseKey(component.aircraft_id),
    partBalancesBaseKey,
    partStockBaseKey,
  ],
})

export const useUpdateFittedComponentMutation = makeAxiosMutation({
  serviceCall: FittedComponentService.updateItem,
  invalidates: (_, component) => [
    fittedComponentsBaseKey(component.aircraft_id),
  ],
})

export const useDeleteFittedComponentMutation = makeAxiosMutation({
  serviceCall: FittedComponentService.deleteItem,
  invalidates: (_, component) => [
    fittedComponentsBaseKey(component.aircraft_id),
    partBalancesBaseKey,
    partStockBaseKey,
  ],
})

function modStatusKeyOf(saved: ModStatusPublic): QueryKey {
  return saved.aircraft_id
    ? modStatusBaseKey(saved.aircraft_id)
    : modStatusRootKey
}

export const useCreateModStatusMutation = makeAxiosMutation({
  serviceCall: ModStatusService.createItem,
  invalidates: (_, saved) => [modStatusKeyOf(saved)],
})

export const useUpdateModStatusMutation = makeAxiosMutation({
  serviceCall: ModStatusService.updateItem,
  invalidates: (_, saved) => [modStatusKeyOf(saved)],
})
